import { Bucket } from '../bucket';
import { IList } from '../list';
import { ITgCommand } from '../tgCommand';
import { IField } from '../form';
import { ILoadingStageModel } from '../loadingStage';
import { IOpenState } from '../state';

import {
  ChannelKind,
  ChannelKindType,
  ChannelTokenScope,
  ChannelTokenStatus,
  CommonChannelKind,
  ChannelStatus,
  WzConnectionKind,
  WzOAuthError,
  WzRevokedReason,
  WzRoutingState
} from './server';

export enum WzOAuthClientError {
  resultUnknown = 'result_unknown',
  popupBlocked = 'popup_blocked'
}

export type WzOAuthErrorCode = WzOAuthError | WzOAuthClientError;

export interface IChannelBasic {
  id: string;
  externalId: string;
  domain: string;
  name: string;
  url: string;
  usersTotal: number;
  tokens: IList<IChannelToken>;
  photoUrl: string | null;
  rootChatId: string;
  // значения backend ChannelStatus: общий статус канала, у виджета — своя логика на его основе
  status?: ChannelStatus;
}

export interface IWazzupChannelBasic extends IChannelBasic {
  wzConnectionKind: WzConnectionKind;
  wzRevokedReason: WzRevokedReason | null;
  wzRoutingState: WzRoutingState | null;
  wzMigrationAvailable: boolean;
}

export interface IChannelBase extends IChannelBasic {
  linked: boolean;
  scenarioIds: IField<string[]>;
}

export interface IChannelTgButton {
  readonly text: IField;
  readonly url: IField;
  readonly saved: IField<boolean>;
  readonly removingStage: ILoadingStageModel;
  readonly updatingStage: ILoadingStageModel;
  readonly isError: boolean;
}

export interface IChannelEnabled extends IChannelBasic {
  kind: ChannelKind;
  enabled: boolean;
}

export type ChannelTokenRevokingOptions =
  | {
      value: true;
      revokedAt: Date;
    }
  | { value: false };

export interface IChannelToken {
  id: string;
  channelId: string;
  scopes: ChannelTokenScope[];
  revokingOptions: ChannelTokenRevokingOptions;
  status: ChannelTokenStatus;
  error: string | null;
  readonly removingStage: ILoadingStageModel;
}

export interface IBaseChannelModel extends IChannelBase {
  readonly removingStage: ILoadingStageModel;
  readonly runChannelStage: ILoadingStageModel;
  readonly newToken: IField;
  readonly creatingNewTokenStage: ILoadingStageModel;
  readonly hasNoTokens: boolean;
  readonly hasTokenErrors: boolean;
  readonly hasManyTokenErrors: boolean;

  removeToken(id: string): Promise<BaseResponse>;
  runFromBlock(params: { blockId: string; bucket: Bucket }): Promise<void>;
  addToken(token: IChannelToken): void;
  createToken(): Promise<BaseResponse>;
  handleChangeScenarioIds(scenarioId: string): void;
  updateScenarioIds(scenarioIds: string[]): Promise<BaseResponse>;
}

export interface ICommonChannelModel extends IBaseChannelModel {
  kind: CommonChannelKind;
}

export interface IWazzupChannelModel
  extends IBaseChannelModel, IWazzupChannelBasic {
  kind: ChannelKind.WZ;
  readonly isWzOAuth: boolean;
  readonly canMigrateToOAuth: boolean;
  readonly revokedReason: string | null;
}

export interface ITgChannelModel extends IBaseChannelModel {
  kind: ChannelKind.TELEGRAM;
  commands: IList<ITgCommand>;
  button: IChannelTgButton;
  updateButton(): Promise<BaseResponse>;
  removeButton(): Promise<BaseResponse>;
  removeCommand(id: string): Promise<BaseResponse>;
  updateCommand(id: string): Promise<BaseResponse>;
  readonly selectedCommandId: IField<string | null>;
  readonly selectedCommand: ITgCommand;
  unselectCommand(): void;
  readonly commandsFormState: IOpenState;
  saveCommand(): Promise<void>;
  closeCommand(): void;
  /** Доставляет ли бот custom emoji; `null` — таких отправок ещё не было */
  readonly customEmojiSupported: boolean | null;
  setCustomEmojiSupported(value: boolean | null): void;
}

export interface IWidgetChannelModel extends IBaseChannelModel {
  kind: ChannelKind.WIDGET;
  apiKey: IField<string>;
  generatedSnippet: string;
  status: ChannelStatus;
  isChecking: boolean;
  isInstallCheckBlocked: boolean;
  checkInstall(): Promise<boolean>;
}

export type IChannelModel =
  | ITgChannelModel
  | IWidgetChannelModel
  | IWazzupChannelModel
  | ICommonChannelModel;

export interface IChannelCreationStore {
  readonly isError: boolean;
  selectedPlatform: ChannelKindType | null;

  save(): Promise<BaseResponse>;
}

export enum MobileChannelsModalTab {
  createChannel = 'createChannel',
  selectChannels = 'selectChannels'
}

export enum PubSubChannelEvent {
  removeScenarioId = 'removeScenarioId',
  addScenarioId = 'addScenarioId'
}

export type PubSubHandleScenarioEventPayload = {
  channelId: string;
  scenarioId: string;
};
